<template>
  <FormGroup :scope="scope" :starts-enabled="!isInitialValueBlank || null" :errors="errors" :disable-margin="jumbotronConfig || null" :hide-label="hideLabel || null" >
    <template #disabled="{inputId, inputName}">
      <input v-for="locale in locales" :key="`hidden_${inputName}[${locale}]`" type="hidden" :name="`${inputName}[${locale}]`" value="">
    </template>
    <template #default="{inputId, inputName, label}">
      <div class="d-flex flex-column" :class="{ 'is-invalid': anyErrors }">
        <ul :class="`nav nav-tabs position-relative border-bottom-0 ${jumbotronConfig ? 'bg-trumbowyg rounded-0 pt-2 ps-2' : ''}`"
          role="tablist" ref="tablist" @click="restartBtnBackground">
          <li
            v-for="(locale, i) in locales"
            :key="`${inputId}_${locale}_tab`"
            class="nav-item"
            role="presentation"
          >
            <label
              class="nav-link py-1 border-bottom-0"
              :class="{
                active: i === 0,
                'bg-trumbowyg': i === 0 && !jumbotronConfig,
                'bg-trumbowyg-dark': i === 0 && jumbotronConfig,
                'tab-invalid': hasError(locale),
              }"
              :for="`${inputId}_${locale}`"
              data-bs-toggle="tab"
              :data-bs-target="`#${inputId}_${locale}_pane`"
              type="button"
              role="tab"
              :aria-controls="`${inputId}_${locale}_pane`"
              :aria-selected="i === 0 || null"
            >
              <span aria-hidden="true">{{ $t(`languages.${locale}`) }}</span>
              <span class="visually-hidden">
                {{ $t("backend.candidacy.forms.form.name_in_language", { name: label, language: $t(`languages.${locale}`) }) }}
              </span>
            </label>
          </li>
        </ul>

        <div class="tab-content">
          <div
            v-for="(locale, i) in locales"
            :key="`${inputId}_${locale}_pane`"
            :class="{
              'tab-pane': true,
              show: i === 0,
              active: i === 0,
            }"
            :id="`${inputId}_${locale}_pane`"
            role="tabpanel"
            :aria-labelledby="`${inputId}_${locale}_tab`"
            tabindex="0"
          >
            <div
              class="form-control p-0"
              :class="{
                'is-invalid': hasError(locale),
                'input-changed': wasChanged(locale),
                'mb-2': !jumbotronConfig,
                'rounded-0': jumbotronConfig,
                'rounded-start-top-0': i === 0,
              }"
            >
              <Trumbowyg
                :value="currentValue[locale]"
                :id="`${inputId}_${locale}`"
                :placeholder="placeholder"
                :name="`${inputName}[${locale}]`"
                :config="wysiwygConfiguration"
                @input="handleInput($event, locale)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </FormGroup>
</template>

<script lang="ts">
import FormGroup from "@/components/backend/candidacy/form_builder/form_items/shared/FormGroup.vue";
import Trumbowyg from "@/components/shared/trumbowyg/Trumbowyg.vue";
import wysiwygConfig from "@/entrypoints/backend/wysiwyg";
import { humanize, scopeToId } from "@/lib/candidacy/form_builder/utils";
import type { LocalString, PropType, Locale } from "@/types";
import { defineComponent } from "vue";

export default defineComponent({
  name: "LocaleRichTextField",
  components: { Trumbowyg, FormGroup },
  emits: ["update"],
  props: {
    initialValue: {
      type: Object as PropType<LocalString>,
      default: {}
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    },
    locales: {
      type: Array as PropType<Locale[]>,
      default: ["en"],
    },
    placeholder: {
      type: String,
      default: "",
    },
    errors: {
      type: Array as PropType<any[]>,
      default: [],
    },
    jumbotronConfig: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    }
  },
  data: ({ initialValue, locales }) => ({
    currentValue: Object.fromEntries(locales.map(l => [l, initialValue[l]])),
  }),
  methods: {
    scopeToId,
    humanize,
    wasChanged(locale) {
      return this.initialValue[locale] !== this.currentValue[locale];
    },
    hasError(locale) {
      return this.errors.some(error => error.path?.includes(locale));
    },
    restartBtnBackground(event) {
      if (event.target.tagName === "SPAN" || event.target.tagName === "LABEL" && !event.target.disabled) {
        Array.from(this.$refs.tablist.children).forEach((li: HTMLElement) => {
          if (!this.jumbotronConfig) li.children[0].classList.remove("bg-trumbowyg");
          else li.children[0].classList.remove("bg-trumbowyg-dark");
        });

        const className = this.jumbotronConfig ? "bg-trumbowyg-dark" : "bg-trumbowyg";
        if (event.target.tagName === "SPAN") event.target.parentElement.classList.add(className);
        else event.target.classList.add(className);
      }
    },
    handleInput(event, locale) {
      this.currentValue[locale] = event.target?.value ?? event
    }
  },
  computed: {
    wysiwygConfiguration() {
      return this.jumbotronConfig ? wysiwygConfig : {};
    },
    isInitialValueBlank() {
      return Object.values(this.initialValue).every(v => !v);
    },
    anyErrors() {
      return this.errors.length > 0;
    },
  },
  watch: {
    currentValue: {
      handler: function () {
        this.$emit("update", this.currentValue);
      },
      deep: true,
    },
  },
  mounted() {
    this.$emit("update", this.currentValue);
  },
});
</script>

<style scoped>
.bg-trumbowyg {
  background-color: #ecf0f1 !important;
}

.bg-trumbowyg-dark {
  background-color: #d7e0e2 !important;
}

.rounded-start-top-0 {
  border-start-start-radius: 0;
}

.tab-invalid, .tab-invalid.active, .tab-invalid:hover {
  color: var(--bs-form-invalid-border-color);
}

.tab-invalid.active {
  border: var(--bs-form-invalid-border-color) solid 1px !important;
  border-bottom-color: transparent !important;
}

.tab-invalid:not(.active):hover {
  border: rgb(from var(--bs-form-invalid-border-color) r g b / 20%) solid 1px !important;
  border-bottom-width: 0 !important;
}
</style>
